import { parseGermanAmount, parseGermanDate } from "./german";
import type { Transaction } from "./types";

const DATE_RE = String.raw`(\d{2}\.\d{2}\.\d{4})`;
/** German amount; pdf.js often emits "800 ,00" with a space before the decimal comma. */
const AMOUNT_CORE = String.raw`[+-]?(?:\d{1,3}(?:\.\d{3})*|\d+)\s*,\s*\d{2}`;
const AMOUNT_RE = String.raw`(${AMOUNT_CORE})\s*EUR`;
const ROW_RE = new RegExp(
  `^${DATE_RE}\\s+${DATE_RE}\\s+(.+?)\\s+${AMOUNT_RE}\\s*$`,
);
/** LBS duplicate amount; pdf.js may truncate the trailing " EUR". */
const LBS_BETRAG_SUFFIX = /\s+Betrag:.*$/i;

export function normalizeExtractedTransactionLine(rawLine: string): string {
  let line = rawLine.split(/\s+/).join(" ");
  line = line.replace(/(\d)\s+,\s*(\d{2})/g, "$1,$2");
  return line.replace(LBS_BETRAG_SUFFIX, "").trim();
}

export function parseTransactionsFromText(text: string): Transaction[] {
  const transactions: Transaction[] = [];

  for (const rawLine of text.split(/\r?\n/)) {
    const line = normalizeExtractedTransactionLine(rawLine);
    const match = ROW_RE.exec(line);
    if (!match) continue;

    const [, bookingS, valueS, txText, amountS] = match;
    transactions.push({
      booking_date: parseGermanDate(bookingS),
      value_date: parseGermanDate(valueS),
      text: txText.trim(),
      amount: parseGermanAmount(amountS),
      source: "pdf",
    });
  }

  const unique = new Map<string, Transaction>();
  for (const tx of transactions) {
    const key = `${tx.booking_date}|${tx.value_date}|${tx.text}|${tx.amount.toFixed(2)}`;
    unique.set(key, tx);
  }

  return [...unique.values()].sort(compareTransactions);
}

export function compareTransactions(a: Transaction, b: Transaction): number {
  const v = a.value_date.localeCompare(b.value_date);
  if (v !== 0) return v;
  const bkg = a.booking_date.localeCompare(b.booking_date);
  if (bkg !== 0) return bkg;
  const t = a.text.localeCompare(b.text);
  if (t !== 0) return t;
  return a.amount - b.amount;
}

export function transactionDateRange(txs: Transaction[]): {
  earliest: string;
  latest: string;
} {
  if (txs.length === 0) {
    throw new Error("No transactions");
  }
  let earliest = txs[0].value_date;
  let latest = txs[0].value_date;
  for (const tx of txs) {
    if (tx.value_date < earliest) earliest = tx.value_date;
    if (tx.value_date > latest) latest = tx.value_date;
  }
  return { earliest, latest };
}
